import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, X, AlertCircle, RefreshCw } from 'lucide-react';
import {
  listNotifications,
  getUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
} from '../../services/notificationsService';

const POLL_INTERVAL_MS = 60_000;

/**
 * Header bell that lists in-app notifications and surfaces unread count.
 * Used on the ExecDashboard so EAs see exec-posted notes immediately.
 */
const NotificationBell = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0, width: 380 });

  const updatePanelPosition = useCallback(() => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const margin = 8;
    const desiredWidth = Math.min(380, window.innerWidth - 2 * margin);
    let left = rect.right - desiredWidth;
    if (left < margin) left = margin;
    setPanelPos({
      top: rect.bottom + margin,
      left,
      width: desiredWidth,
    });
  }, []);

  const refreshCount = useCallback(async () => {
    try {
      const c = await getUnreadCount();
      setUnread(c);
    } catch (e) {
      console.warn('NotificationBell: failed to fetch unread count', e);
    }
  }, []);

  const refreshItems = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [recent, totalUnread] = await Promise.all([
        listNotifications({ limit: 25 }),
        getUnreadCount().catch(() => null),
      ]);
      const safeList = Array.isArray(recent) ? recent : [];
      const recentUnread = safeList.filter(n => !n.read_at).length;

      // If unread notifications exist outside the recent window, pull them in
      // explicitly so the dropdown contents match the badge count.
      let merged = safeList;
      if (typeof totalUnread === 'number' && totalUnread > recentUnread) {
        try {
          const unreadList = await listNotifications({ unreadOnly: true, limit: 50 });
          const seen = new Set(safeList.map(n => n.id).filter(Boolean));
          const extras = (Array.isArray(unreadList) ? unreadList : []).filter(
            n => n && n.id && !seen.has(n.id)
          );
          merged = [...extras, ...safeList].sort((a, b) => {
            const ta = a?.created_at ? new Date(a.created_at).getTime() : 0;
            const tb = b?.created_at ? new Date(b.created_at).getTime() : 0;
            return tb - ta;
          });
        } catch (innerErr) {
          console.warn('NotificationBell: failed to fetch unread fallback', innerErr);
        }
      }

      setItems(merged);
      setUnread(
        typeof totalUnread === 'number'
          ? totalUnread
          : merged.filter(n => !n.read_at).length
      );
      setHasLoaded(true);
    } catch (e) {
      console.warn('NotificationBell: failed to load notifications', e);
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      setLoadError(
        status
          ? `Couldn't load notifications (${status}${detail ? `: ${detail}` : ''})`
          : "Couldn't load notifications. Check your connection and try again."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshCount();
    const t = setInterval(refreshCount, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [refreshCount]);

  useEffect(() => {
    if (open) refreshItems();
  }, [open, refreshItems]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      const inTrigger = containerRef.current && containerRef.current.contains(e.target);
      const inPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!inTrigger && !inPanel) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // Keep the portaled panel anchored to the bell button while it is open.
  useLayoutEffect(() => {
    if (!open) return;
    updatePanelPosition();
    const onChange = () => updatePanelPosition();
    window.addEventListener('resize', onChange);
    window.addEventListener('scroll', onChange, true);
    return () => {
      window.removeEventListener('resize', onChange);
      window.removeEventListener('scroll', onChange, true);
    };
  }, [open, updatePanelPosition]);

  const handleMarkRead = async (id) => {
    try {
      await markNotificationRead(id);
      setItems(prev => prev.map(n => n.id === id ? { ...n, read_at: new Date().toISOString() } : n));
      setUnread(prev => Math.max(0, prev - 1));
    } catch (e) { /* silent */ }
  };

  const handleMarkAll = async () => {
    try {
      await markAllNotificationsRead();
      const now = new Date().toISOString();
      setItems(prev => prev.map(n => n.read_at ? n : { ...n, read_at: now }));
      setUnread(0);
    } catch (e) { /* silent */ }
  };

  const formatRelative = (iso) => {
    if (!iso) return '';
    const then = new Date(iso);
    const diffMs = Date.now() - then.getTime();
    const min = Math.floor(diffMs / 60000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const d = Math.floor(hr / 24);
    if (d < 7) return `${d}d ago`;
    return then.toLocaleDateString();
  };

  const buttonClass = 'relative p-2 rounded-lg text-ui-ink-2 hover:text-ui-ink hover:bg-ui-hover/70 transition-colors';

  const panelClass = 'bg-ui-surface border border-ui-line rounded-xl shadow-ui-pop overflow-hidden';

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        className={buttonClass}
        aria-label="Notifications"
        aria-expanded={open}
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-ui-red-strong text-ui-on-primary text-[10px] font-bold flex items-center justify-center">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && typeof document !== 'undefined' && createPortal(
        <div
          ref={panelRef}
          className={panelClass}
          style={{
            position: 'fixed',
            top: panelPos.top,
            left: panelPos.left,
            width: panelPos.width,
            zIndex: 1000,
          }}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-ui-divider">
            <span className="text-sm font-semibold text-ui-ink">Notifications</span>
            <div className="flex items-center gap-2">
              {unread > 0 && (
                <button
                  onClick={handleMarkAll}
                  className="text-xs text-ui-link hover:text-ui-blue-fg"
                  title="Mark all as read"
                >
                  Mark all read
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="text-ui-muted hover:text-ui-ink"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div style={{ maxHeight: '60vh', overflowY: 'auto', minHeight: 60 }}>
            {loading && items.length === 0 ? (
              <div className="text-ui-muted" style={{ padding: '24px 16px', textAlign: 'center', fontSize: 12 }}>Loading…</div>
            ) : loadError ? (
              <div className="text-ui-ink-2" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '24px 16px' }}>
                <AlertCircle className="w-5 h-5 text-ui-amber-solid" />
                <div style={{ fontSize: 12, textAlign: 'center' }}>{loadError}</div>
                <button
                  type="button"
                  onClick={refreshItems}
                  className="bg-ui-hover text-ui-ink"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, padding: '4px 8px', borderRadius: 4, border: 'none', cursor: 'pointer' }}
                >
                  <RefreshCw className="w-3 h-3" /> Retry
                </button>
              </div>
            ) : items.length === 0 ? (
              <div className="text-ui-muted" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '32px 16px' }}>
                <div style={{ fontSize: 12 }}>{hasLoaded && unread > 0 ? "Notifications are out of sync" : "You're all caught up"}</div>
                {hasLoaded && unread > 0 && (
                  <button
                    type="button"
                    onClick={refreshItems}
                    className="bg-ui-hover text-ui-ink"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, padding: '4px 8px', borderRadius: 4, border: 'none', cursor: 'pointer' }}
                  >
                    <RefreshCw className="w-3 h-3" /> Refresh
                  </button>
                )}
              </div>
            ) : (
              items.map((n, idx) => {
                const unreadItem = !n.read_at;
                const title = n.title || 'Notification';
                const rowStyle = {
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  padding: '12px 16px',
                  cursor: n.link_url ? 'pointer' : 'default',
                };
                const onRowClick = () => {
                  if (unreadItem) handleMarkRead(n.id);
                  setOpen(false);
                  if (n.link_url) navigate(n.link_url);
                };
                return (
                  <div
                    key={n.id || `notif-${idx}`}
                    className={`border-b border-ui-divider ${unreadItem ? 'bg-ui-hover/60' : 'bg-transparent'}`}
                    style={rowStyle}
                    onClick={onRowClick}
                    role={n.link_url ? 'button' : undefined}
                    tabIndex={n.link_url ? 0 : undefined}
                    onKeyDown={(e) => { if (n.link_url && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onRowClick(); } }}
                  >
                    {unreadItem && (
                      <span className="bg-ui-blue-solid" style={{ marginTop: 6, width: 8, height: 8, borderRadius: '9999px', flexShrink: 0 }} />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="text-ui-ink" style={{ fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>
                      {n.body && (
                        <div className="text-ui-ink-2" style={{ fontSize: 12, marginTop: 2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{n.body}</div>
                      )}
                      {n.created_at && (
                        <div className="text-ui-muted" style={{ fontSize: 10, marginTop: 4 }}>{formatRelative(n.created_at)}</div>
                      )}
                    </div>
                    {unreadItem && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleMarkRead(n.id); }}
                        className="text-ui-muted"
                        style={{ padding: 4, borderRadius: 4, background: 'transparent', border: 'none', cursor: 'pointer', flexShrink: 0 }}
                        title="Mark as read"
                      >
                        <Check className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default NotificationBell;
